import Radio from '@mui/material/Radio';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Button } from '@src/common/components/button/Button';
import { FilterableTable } from '@src/common/components/FilterableTable';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { useReplacePermitTypeNavigationState } from '@src/orders/components/permitList/PermitActions/ReplacePermitTypeNavigationState';
import type { PermitType, PermitTypeList } from '@src/orders/domain/PermitType';
import { useCompatiblePermitTypes } from '@src/orders/hooks/useCompatiblePermitTypes';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { useReplacePermitType } from '@src/orders/hooks/useReplacePermitType';
import { OrdersNavigation } from '@src/routing/OrdersNavigation';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props {
  orderId: string;
  originPermitId: string;
}

export const ReplacePermitApplicationType = ({
  orderId,
  originPermitId,
}: Props) => {
  const { t } = useTranslation();

  return (
    <PageLayout title={t('permitApplication.replaceType.title')}>
      <PermitTypeSelectionTable
        orderId={orderId}
        originPermitId={originPermitId}
      />
    </PageLayout>
  );
};

const PermitTypeSelectionTable = ({ orderId, originPermitId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showErrorToast } = useToast();
  const { refreshOrder } = useRefreshOrder({ id: orderId });
  const { note } = useReplacePermitTypeNavigationState();

  const { permitTypes } = useCompatiblePermitTypes(originPermitId);

  const [selectedTypeId, setSelectedTypeId] = useState<string | undefined>(
    undefined,
  );

  const navigateToOrderForm = () => {
    refreshOrder();
    navigate(OrdersNavigation.edit.url(orderId));
  };

  const { replacePermitType, isPending } = useReplacePermitType({
    permitApplicationId: originPermitId,
    onSuccess: navigateToOrderForm,
    onError: () => showErrorToast(t('permitApplication.replaceType.error')),
  });

  const submitSelection = () => {
    const selected = permitTypes.permitTypes.find(
      (p) => p.id === selectedTypeId,
    );
    if (!selected) return;
    replacePermitType({
      newPermitTypeOriginalId: selected.originalId,
      note,
    });
  };

  const rows = buildRows(permitTypes);
  const columns = generateColumns(t, selectedTypeId, setSelectedTypeId);

  return (
    <Stack direction="column" gap={theme.spacing['3']}>
      <FilterableTable
        paginationAndSortMode="client"
        ariaLabel="replace permit type table"
        columns={columns}
        rows={rows}
        onRowClick={(params) => setSelectedTypeId(String(params.id))}
      />

      <Button disabled={isPending || !selectedTypeId} onClick={submitSelection}>
        {t('next')}
      </Button>
    </Stack>
  );
};

const buildRows = (permitTypes: PermitTypeList) =>
  permitTypes.permitTypes.map((permitType: PermitType) => ({
    id: permitType.id,
    name: permitType.name,
    type: permitType.type,
    jurisdiction: permitType.jurisdiction,
  }));

const generateColumns = (
  t: TFunction,
  selectedTypeId: string | undefined,
  onSelect: (typeId: string) => void,
): GridColDef[] => [
  {
    field: 'select',
    headerName: '',
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    hideable: false,
    flex: 0,
    minWidth: 60,
    renderCell: (params) => (
      <Radio
        checked={selectedTypeId === params.row.id}
        onChange={() => onSelect(String(params.row.id))}
        onClick={(e) => e.stopPropagation()}
        value={params.row.id}
        inputProps={{ 'aria-label': params.row.name }}
      />
    ),
  },
  { field: 'name', headerName: t('permitType.name'), flex: 1 },
  { field: 'type', headerName: t('permitType.type'), flex: 1 },
  {
    field: 'jurisdiction',
    headerName: t('permitType.jurisdiction'),
    flex: 1,
  },
];
